/**
 * Live Your Fun - Design Token Colors
 *
 * Single source of truth for hex color values, consumed by:
 * - src/styles/theme.css (CSS custom properties for light/dark themes on web)
 * - Future: React Native theme (mobile)
 *
 * On web, Tailwind colors in tailwind.config.js resolve to CSS variables
 * (`rgb(var(--color-*) / <alpha-value>)`), and `src/styles/theme.css` defines
 * those variables for both :root (light) and .dark (dark mode) scopes. If you
 * change a hex value here, update the matching RGB triplet in theme.css too.
 *
 * Naming Convention:
 * - Semantic names (primary, accent, surface) over color names (blue, yellow)
 * - Each color has DEFAULT plus variants (dark, light, lighter)
 * - The `dark` suffix on structural tokens (surface.dark, background.dark,
 *   border.dark) means "always-dark" values used for tooltips, admin sidebar,
 *   overlay badges — NOT the dark-mode auto-flip value. The auto-flip happens
 *   via CSS variables in theme.css.
 */

const colors = {
  // ==========================================================================
  // BRAND COLORS
  // ==========================================================================

  /**
   * Primary - Harbour Blue (visual refresh, 2026-10-01; was #3358d4)
   * Means "you can tap this / you are here": primary buttons, links, the
   * active state. Not for decoration — badges and avatars go neutral or tinted.
   */
  primary: {
    DEFAULT: '#1f3fa3',    // Harbour blue — 9:1 with white text
    dark: '#162e7a',       // Deep harbour - hover/pressed
    light: '#5a77d6',      // Light harbour - hover outlines, focus rings
    lighter: '#dde4f6',    // Pale harbour - tinted backgrounds, highlights
    deep: '#162e7a',       // Static deep blue TEXT that never flips in dark mode —
                           // for text on backgrounds that stay light (solid gold
                           // bg-accent, bg-white/90 glass). Same hex as `dark`,
                           // but exempt from the dark-mode -dark text flip.
  },

  /**
   * Coral - the warm accent (visual refresh, 2026-10-01)
   * Used for: small warm moments — countdowns ("In 4 days"), celebrations,
   * a featured highlight. A FILL only: text on coral is `coral.ink`, and coral
   * is never text on a light surface and never a status colour (that's error).
   */
  coral: {
    DEFAULT: '#ff7a59',    // Coral fill
    dark: '#e5603f',       // Hover/pressed fill
    light: '#ffe3da',      // Pale coral background
    ink: '#3a1206',        // Text on a coral fill (7:1); static, never flips
  },

  /**
   * Accent - Gold/Yellow
   * Used for: star ratings, review highlights, "highlight" glyphs. Since the
   * 2026-10-01 refresh gold is the RATING colour; warm moments take coral.
   */
  accent: {
    DEFAULT: '#ecc94b',    // Gold (Tailwind yellow-400)
    dark: '#d69e2e',       // Darker gold (Tailwind yellow-600) - hover states
    light: '#faf089',      // Light yellow (Tailwind yellow-200) - subtle highlights
  },

  // ==========================================================================
  // NEUTRAL COLORS
  // ==========================================================================

  /**
   * Text Colors
   * Cool slate ramp to sit on the Mist background (visual refresh 2026-10-01;
   * was a warm stone ramp).
   */
  text: {
    primary: '#1e2632',    // Cool near-black - main text
    secondary: '#5f6b7a',  // Slate - secondary text (4.7:1 on Mist, 5.4:1 on white)
    muted: '#8a94a3',      // Light slate - muted, placeholders
    inverse: '#ffffff',    // White - text on dark backgrounds
  },

  /**
   * Surface Colors
   * Used for: Cards, modals, panels, elevated elements
   */
  surface: {
    DEFAULT: '#ffffff',    // White - frames: photo cards, list groups, sheets, modals
    secondary: '#f3f5f8',  // Pale slate - skeleton backgrounds, card placeholders, secondary panels
    tertiary: '#e3e8ee',   // Slate - hover states on secondary surfaces, disabled backgrounds
    dark: '#1e2632',       // Cool dark - always-dark surface (tooltips, admin sidebar)
  },

  /**
   * Background Colors
   * Used for: Page backgrounds, sections
   */
  background: {
    DEFAULT: '#edf0f4',    // Mist - page bg (light mode); white frames sit on it
    dark: '#11151b',       // Cool near-black - always-dark bg
  },

  /**
   * Border Colors
   */
  border: {
    DEFAULT: '#dae0e8',    // Slate hairline
    dark: '#3a4350',       // Always-dark border
  },

  // ==========================================================================
  // SEMANTIC / FEEDBACK COLORS
  // ==========================================================================

  /**
   * Success - Green
   * Used for: Success states, confirmations, positive actions
   */
  success: {
    DEFAULT: '#2f9e6a',    // Grass green - distinct from framework defaults
    light: '#dcf3e6',      // Pale green - backgrounds
    dark: '#1b6e45',       // Deep green - text on light bg (AA on success-light)
  },

  /**
   * Warning - Amber
   * Used for: Warnings, cautions, pending states
   */
  warning: {
    DEFAULT: '#e08a2b',    // Warm amber
    light: '#fbe8cf',      // Pale amber - backgrounds
    dark: '#9a5a12',       // Deep amber - text on light bg (AA on warning-light)
  },

  /**
   * Error - Red
   * Used for: Errors, destructive actions, alerts
   */
  error: {
    DEFAULT: '#d93a55',    // Crimson - cooler than coral so an error never reads as the accent
    light: '#fbe3e8',      // Pale crimson - backgrounds
    dark: '#a3213a',       // Deep crimson - text on light bg (AA on error-light)
  },

  /**
   * Info - Blue
   * Used for: Informational messages, tips, neutral highlights
   */
  info: {
    DEFAULT: '#2f80c4',    // Azure - informational (distinct from brand primary)
    light: '#dbeafb',      // Pale azure - backgrounds
    dark: '#1d5f93',       // Deep azure - text on light bg (AA on info-light)
  },

  // ==========================================================================
  // FEATURE COLORS
  // ==========================================================================

  /**
   * Tour - Blue (aliased to the primary brand blue)
   * Walking tours no longer carry a bespoke accent color. The `tour` token is
   * kept so existing `bg-tour`/`text-tour` classes need no rename, but it now
   * resolves to the primary blue family for consistency with the rest of the app.
   * Used for: Walking tours, curated experiences, tour stop badges, tour action buttons
   */
  tour: {
    DEFAULT: '#1f3fa3',    // = primary.DEFAULT — badges, stop numbers, primary actions
    dark: '#162e7a',       // = primary.dark — text on light backgrounds, hover states
    light: '#dde4f6',      // = primary.lighter — backgrounds, info banners, highlights
    border: '#5a77d6',     // = primary.light — borders, hover outlines
  },

  // ==========================================================================
  // CATEGORY COLORS (for maps, activity types, etc.)
  // ==========================================================================

  /**
   * Category Colors
   * Used for: Map markers, activity type badges, visual differentiation
   */
  category: {
    accommodation: '#3576d6',  // Blue
    restaurant: '#e2503e',     // Red
    activity: '#14a17a',       // Teal-green
    custom: '#7c52e0',         // Violet (tracks the tour token family)
    transport: '#e8920f',      // Amber
    default: '#5f6b7a',        // Slate (matches content-secondary)
  },

  /**
   * Time-of-Day Colors
   * Used for: the itinerary's Morning / Afternoon / Evening bucket rails and
   * the Map tab's day-view markers and legend — ONE definition so a stop's
   * colour on the map is its bucket's colour in the list. Static (not
   * themed), like category colours: they are wayfinding, never text.
   * Deliberately not the warm accent — coral means Today (gold before the
   * 2026-10-01 refresh).
   */
  timeBlock: {
    morning: '#e8920f',        // Amber (= category.transport)
    afternoon: '#0f8fa0',      // Teal (2026-11-05; was the pre-2026-10 primary #3358d4, which met the brand navy and the Up-next node on one card)
    evening: '#7c52e0',        // Violet (= category.custom)
    allDay: '#14a17a',         // Teal-green (= category.activity) — map only
  },

  // ==========================================================================
  // CONTRIBUTOR LEVEL COLORS (gamification)
  // ==========================================================================

  /**
   * Contributor Levels
   * Used for: User contribution badges, XP-based rankings
   */
  contributor: {
    expert: '#8a3fd4',       // Violet - 1000+ XP
    advanced: '#2f6fe0',     // Blue - 500+ XP
    experienced: '#0f9d6a',  // Green - 200+ XP
    active: '#dd8526',       // Amber - 50+ XP
    newcomer: '#5f6b7a',     // Slate - < 50 XP (matches content-secondary)
  },
};

/**
 * Dark-mode overrides (for documentation + future React Native theming).
 * Mirrors the .dark selector in src/styles/theme.css. Only tokens listed here
 * flip in dark mode — everything else inherits from the light palette above.
 */
const darkMode = {
  content: {
    primary: '#f3f5f8',   // cool near-white — primary text on dark surfaces
    secondary: '#a3adbb', // slate — secondary text
    muted: '#6f7a89',     // dim slate — muted text
    // inverse stays #ffffff
  },
  surface: {
    DEFAULT: '#1a2029',   // cool dark — frames in dark mode
    secondary: '#151a22', // slightly darker for skeletons/secondary panels
    tertiary: '#262e39',  // lighter for hover
  },
  background: {
    DEFAULT: '#11151b',   // cool near-black — page bg
  },
  border: {
    DEFAULT: '#2e3744',   // cool dark border
    subtle: 'rgba(255, 255, 255, 0.08)',
  },
  primary: {
    lighter: '#1b2a5e',   // dark harbour tint for backgrounds that were pale in light mode
  },
  coral: {
    light: '#4a2318',     // dark coral bg
  },
  accent: {
    light: '#713f12',     // yellow-900 dark gold bg
  },
  success: {
    light: '#16432c',     // dark green bg for success banners
  },
  warning: {
    light: '#5e3a12',     // dark amber bg
  },
  error: {
    light: '#5a1f2b',     // dark crimson bg
  },
  info: {
    light: '#173a5c',     // dark blue bg
  },
  tour: {
    light: '#173a5c',     // = primary dark-mode light — dark blue bg (tour aliased to blue)
  },

  /**
   * "-dark" TEXT flips (text-* utilities only — see textColor in
   * tailwind.config.js). In dark mode, text-primary-dark / text-success-dark /
   * etc. become these light tints so "-dark text on -light background" pairings
   * stay readable when the -light backgrounds (above) flip dark. Backgrounds,
   * borders, and gradient stops keep the static light-mode -dark values.
   * primary.deep intentionally does NOT flip.
   */
  darkText: {
    primary: '#9db1f0',      // harbour lightened for dark mode (light mode: #1f3fa3)
    // (Outlines — border-/ring-/outline-primary — flip to #5a77d6 in dark mode
    //  via --color-primary-outline; fills keep #1f3fa3.)
    primaryDark: '#b4c3f4',  // light periwinkle (light mode: #162e7a)
    tour: '#9db1f0',         // = primary
    tourDark: '#b4c3f4',     // = primaryDark
    coralDark: '#ffb29c',    // light coral (light mode: #a63a1e)
    successDark: '#7fd8a4',  // light green (light mode: #1b6e45)
    warningDark: '#f0b661',  // light amber (light mode: #9a5a12)
    errorDark: '#f4a3b2',    // light crimson (light mode: #a3213a)
    infoDark: '#8cc3f0',     // light blue (light mode: #1d5f93)
  },
};

/**
 * Tailwind color mapping reference
 * Only the gold accent still aligns to named Tailwind colors. Everything else
 * (harbour, coral, the Mist slate ramp, the feedback hues) is a bespoke palette
 * and intentionally does NOT map to Tailwind defaults.
 */
const tailwindMapping = {
  '#ecc94b': 'yellow-400',   // accent
  '#d69e2e': 'yellow-600',   // accent-dark
  '#faf089': 'yellow-200',   // accent-light
};

module.exports = { colors, darkMode, tailwindMapping };
